import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useId,
  useRef,
  type MutableRefObject,
} from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useWorkScaleClass } from '@/design-system/work-scale'
import { ScrollArea } from '@/design-system/components/layout/scroll-area'

/**
 * Popover — всплывающая панель по клику: справка «?» рядом с действием, пояснение, в котором есть
 * таблица или список (для короткой подписи — `Tooltip`). На Radix, как Select/Dialog/Tooltip:
 * триггер получает `aria-expanded`/`aria-controls`, фокус переходит в панель и возвращается на
 * триггер, закрытие — по Escape, клику вне панели и кнопке закрытия; панель позиционируется у
 * триггера и не выходит за край окна.
 *
 * Композиция в стиле shadcn: Popover / PopoverTrigger / PopoverContent / PopoverClose.
 * `PopoverContent` с `title` рисует шапку с заголовком и крестиком — готовая «карточка-подсказка»;
 * без `title` — пустая панель под своё содержимое.
 *
 * Сторона и высота: панель открывается снизу, а если там меньше `--max-popover` и сверху места
 * больше — сверху (у триггера внизу окна или модалки панель не сжимается в полоску). Высота — не
 * больше `--max-popover` и места до края окна, ширина — не больше места до края; длинное содержимое
 * прокручивается внутри тела панели (`ScrollArea` ДС), шапка остаётся на месте. Появление —
 * короткий сдвиг с проявлением от триггера (`.ds-popover`), без анимации при
 * `prefers-reduced-motion`.
 */

// Триггер для выбора стороны панели: Radix переворачивает панель, только когда она не помещается,
// а панель, ограниченная местом до края, «помещается» всегда — сторону выбираем сами.
const AnchorContext = createContext<MutableRefObject<HTMLElement | null> | null>(null)

export function Popover(props: React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root>) {
  const anchorRef = useRef<HTMLElement | null>(null)
  return (
    <AnchorContext.Provider value={anchorRef}>
      <PopoverPrimitive.Root {...props} />
    </AnchorContext.Provider>
  )
}

/**
 * Триггер — кнопка `type="button"` по умолчанию (как у `TooltipTrigger`): внутри формы триггер
 * без типа был бы `submit`. Для своей кнопки — `asChild`.
 */
export const PopoverTrigger = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger>
>(({ type = 'button', ...props }, ref) => {
  const anchorRef = useContext(AnchorContext)
  const setRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (anchorRef) anchorRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    },
    [anchorRef, ref],
  )
  return <PopoverPrimitive.Trigger ref={setRef} type={type} {...props} />
})
PopoverTrigger.displayName = 'PopoverTrigger'

export const PopoverClose = PopoverPrimitive.Close

export interface PopoverContentProps extends React.ComponentPropsWithoutRef<
  typeof PopoverPrimitive.Content
> {
  /** Заголовок панели: шапка с крестиком, панель подписана заголовком для AT. */
  title?: string
  /** Подпись крестика для AT. */
  closeLabel?: string
  /** Классы тела панели (под шапкой) — например, отступы. */
  bodyClassName?: string
}

/** Сторона по месту у триггера: снизу, пока там помещается `--max-popover` или сверху не больше. */
function preferredSide(anchor: HTMLElement | null): 'top' | 'bottom' {
  if (!anchor) return 'bottom'
  const rect = anchor.getBoundingClientRect()
  const root = getComputedStyle(document.documentElement)
  const rem = parseFloat(root.fontSize) || 16
  const maxHeight = (parseFloat(root.getPropertyValue('--max-popover')) || 40) * rem
  const below = window.innerHeight - rect.bottom
  const above = rect.top
  return below >= maxHeight || below >= above ? 'bottom' : 'top'
}

export const PopoverContent = forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  PopoverContentProps
>(
  (
    {
      className,
      bodyClassName,
      children,
      title,
      closeLabel = 'Закрыть',
      side,
      align = 'start',
      sideOffset = 8,
      collisionPadding = 16,
      style,
      ...props
    },
    ref,
  ) => {
    // Шкала шрифтов (centrika-docs!31): портал вне DOM-поддерева рабочей области — класс шкалы
    // объявляется на панели, как у SelectContent/TooltipContent.
    const workScale = useWorkScaleClass()
    const titleId = useId()
    const anchorRef = useContext(AnchorContext)
    // Панель монтируется при открытии — сторона выбирается по положению триггера в этот момент.
    const resolvedSide = side ?? preferredSide(anchorRef?.current ?? null)
    return (
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          ref={ref}
          side={resolvedSide}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={collisionPadding}
          aria-labelledby={title ? titleId : undefined}
          style={{
            maxHeight:
              'min(var(--max-popover), var(--radix-popover-content-available-height, var(--max-popover)))',
            maxWidth: 'var(--radix-popover-content-available-width)',
            ...style,
          }}
          className={cn(
            // z-popover — поверх открытого диалога (справка «?» есть и в модалке «Добавить в
            // структуру»); ширина по умолчанию w-96, но не шире места до края окна.
            'ds-surface ds-popover z-popover flex w-96 flex-col overflow-hidden rounded-12 shadow-elevated outline-none',
            workScale,
            className,
          )}
          {...props}
        >
          {title ? (
            <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line py-2.5 pl-4 pr-2">
              <h3 id={titleId} className="text-14 font-semibold text-txt">
                {title}
              </h3>
              <PopoverPrimitive.Close
                aria-label={closeLabel}
                className="ds-focus-ring inline-flex size-7 items-center justify-center rounded-full text-txt-secondary transition-colors hover:bg-hover hover:text-txt"
              >
                <X className="size-4 stroke-current" aria-hidden />
              </PopoverPrimitive.Close>
            </div>
          ) : null}
          <ScrollArea className="min-h-0 flex-1" viewportClassName={bodyClassName}>
            {children}
          </ScrollArea>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    )
  },
)
PopoverContent.displayName = 'PopoverContent'
